import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button, Form, Header, Icon, Message } from 'semantic-ui-react';
import { API } from '../helpers';
import './ComputeSettingsV110.css';

const settingsEndpoint = '/api/compute/v1/settings';
const probeEndpoint = '/api/compute/v1/probe';
const privateEndpoint = 'http://compute-router:18765';
const routerContract = 'alfred.compute.v113/1';
// The shared interceptor logs Axios errors, including credential-bearing request bodies.
const computeAPI = axios.create({
  ...API?.defaults,
  validateStatus: () => true,
  timeout: 20000,
});
export const computeDefaultsV110 = {
  enabled: false,
  transport: 'public_https',
  endpoint: '',
  remote_timeout_ms: 2000,
  connect_timeout_ms: 500,
  min_rows: 100,
  remote_concurrency: 2,
  local_concurrency: 1,
  queue_limit: 16,
  daily_request_limit: 1000,
  free_tier_confirmed: false,
  operations: {
    'statistics.model_rollup': true,
    'statistics.health_reduce': false,
    'statistics.log_money_reduce': false,
    'statistics.usage_merge': false,
  },
};

const numberFields = [
  ['remote_timeout_ms', 100, 10000, '远程超时 (ms)', 'Remote timeout (ms)'],
  ['connect_timeout_ms', 50, 2000, '连接超时 (ms)', 'Connect timeout (ms)'],
  ['remote_concurrency', 1, 4, '远程并发', 'Remote concurrency'],
  ['local_concurrency', 1, 2, '本地并发', 'Local concurrency'],
  ['queue_limit', 1, 32, '队列上限', 'Queue limit'],
  ['min_rows', 0, 20000, '最少数据行数', 'Minimum rows'],
  ['daily_request_limit', 1, 100000, '每日请求上限', 'Daily request limit'],
];
const operations = [
  ['statistics.model_rollup', '模型统计汇总', 'Model rollup'],
  ['statistics.health_reduce', '健康统计汇总', 'Health reduction'],
  ['statistics.log_money_reduce', '日志金额汇总', 'Log money reduction'],
  ['statistics.usage_merge', '用量合并', 'Usage merge'],
];
const counters = [
  ['remote_active', '远程执行中', 'Remote active'],
  ['local_active', '本地执行中', 'Local active'],
  ['queued', '排队中', 'Queued'],
  ['remote_success', '远程成功', 'Remote successes'],
  ['local_success', '本地成功', 'Local successes'],
  ['fallbacks', '本地回退', 'Local fallbacks'],
  ['rejected', '已拒绝', 'Rejected'],
  ['requests_today', '今日请求', 'Requests today'],
];

export const computeTabTextV110 = (language) =>
  language?.startsWith('zh') ? '计算外置' : 'Compute';

// Only the declared public fields can enter the editable draft.
function publicConfig(config) {
  return Object.fromEntries(
    Object.entries(computeDefaultsV110).map(([key, fallback]) => [
      key,
      key === 'operations'
        ? Object.fromEntries(
            operations.map(([op]) => [
              op,
              config.operations?.[op] ?? fallback[op],
            ])
          )
        : key === 'transport' && config[key] === ''
        ? fallback
        : config[key] ?? fallback,
    ])
  );
}

function snapshot(data) {
  if (
    !data?.config ||
    typeof data.config !== 'object' ||
    Array.isArray(data.config) ||
    typeof data.credential_configured !== 'boolean' ||
    !data.status ||
    typeof data.status !== 'object' ||
    Array.isArray(data.status)
  ) {
    throw new Error('invalid_response');
  }
  const config = publicConfig(data.config);
  if (
    typeof config.endpoint !== 'string' ||
    !['public_https', 'router_private'].includes(config.transport) ||
    typeof config.enabled !== 'boolean' ||
    typeof config.free_tier_confirmed !== 'boolean' ||
    numberFields.some(([key]) => !Number.isSafeInteger(config[key])) ||
    operations.some(([key]) => typeof config.operations[key] !== 'boolean')
  ) {
    throw new Error('invalid_response');
  }
  if (
    config.transport === 'router_private' &&
    !/^http:\/\/compute-router:18765\/?$/.test(config.endpoint)
  )
    throw new Error('compute_router_endpoint_invalid');
  return {
    config,
    credential_configured: data.credential_configured,
    status: {
      ...data.status,
      router:
        data.status.router == null ? null : routerSnapshot(data.status.router),
    },
  };
}

function unwrap(response) {
  if (response?.data?.success !== true) {
    const message =
      typeof response?.data?.message === 'string' ? response.data.message : '';
    throw new Error(
      /^[a-z][a-z0-9_]{0,63}$/.test(message) ? message : 'request_failed'
    );
  }
  return response.data.data;
}

function routerSnapshot(router) {
  if (
    !router ||
    router.contract !== routerContract ||
    typeof router.dispatch_enabled !== 'boolean' ||
    typeof router.quota_ready !== 'boolean' ||
    typeof router.reason !== 'string' ||
    !Array.isArray(router.ready_operations) ||
    router.ready_operations.length > 4 ||
    new Set(router.ready_operations).size !== router.ready_operations.length ||
    router.ready_operations.some(
      (name) => typeof name !== 'string' || !name || name.length > 128
    )
  )
    throw new Error('compute_router_contract_invalid');
  const hasReadyOperations = router.ready_operations.length > 0;
  if (
    (router.quota_ready &&
      (!router.dispatch_enabled ||
        !hasReadyOperations ||
        router.reason !== 'ready')) ||
    (!router.quota_ready && router.reason === 'ready') ||
    (hasReadyOperations && (!router.quota_ready || !router.dispatch_enabled))
  )
    throw new Error('compute_router_contract_invalid');
  return {
    contract: router.contract,
    dispatch_enabled: router.dispatch_enabled,
    quota_ready: router.quota_ready,
    ready_operations: [...router.ready_operations],
    reason: /^[a-z][a-z0-9_]{0,63}$/.test(router.reason)
      ? router.reason
      : router.reason === ''
      ? ''
      : 'unknown_router_reason',
  };
}

function probeCapabilities(result, privateTransport) {
  if (
    !result ||
    typeof result.protocol !== 'string' ||
    typeof result.worker_build !== 'string' ||
    !Number.isSafeInteger(result.max_input_bytes) ||
    result.max_input_bytes < 1 ||
    !Number.isSafeInteger(result.max_output_bytes) ||
    result.max_output_bytes < 1 ||
    !Array.isArray(result.operations) ||
    result.operations.length > 4 ||
    result.operations.some(
      (op) =>
        !op ||
        ['name', 'version', 'input_schema', 'output_schema'].some(
          (key) => typeof op[key] !== 'string'
        )
    )
  )
    throw new Error('invalid_response');
  const router =
    privateTransport || result.router != null
      ? routerSnapshot(result.router)
      : null;
  if (router) {
    if (
      result.protocol !== 'alfred.compute/1' ||
      router.ready_operations.some(
        (name) => !result.operations.some((op) => op.name === name)
      )
    )
      throw new Error('compute_router_contract_invalid');
  }
  // Keep only display fields; never retain arbitrary response metadata or reasons.
  return {
    protocol: result.protocol.slice(0, 128),
    worker_build: result.worker_build.slice(0, 128),
    max_input_bytes: result.max_input_bytes,
    max_output_bytes: result.max_output_bytes,
    operations: result.operations.map((op) =>
      Object.fromEntries(
        ['name', 'version', 'input_schema', 'output_schema'].map((key) => [
          key,
          op[key].slice(0, 128),
        ])
      )
    ),
    ...(router ? { router } : {}),
  };
}

export function validateComputeSettingsV110(
  config,
  credentialConfigured,
  credential,
  clearCredential,
  savedEndpoint = config.endpoint,
  savedTransport = config.transport || 'public_https'
) {
  const errors = {};
  const transport = config.transport || 'public_https';
  if (!['public_https', 'router_private'].includes(transport))
    errors.transport = 'transport';
  for (const [key, min, max] of numberFields) {
    const raw = String(config[key] ?? '');
    const value = Number(raw);
    if (
      !/^\d+$/.test(raw) ||
      !Number.isSafeInteger(value) ||
      value < min ||
      value > max
    )
      errors[key] = 'range';
  }
  if (
    !errors.connect_timeout_ms &&
    !errors.remote_timeout_ms &&
    Number(config.connect_timeout_ms) > Number(config.remote_timeout_ms)
  ) {
    errors.connect_timeout_ms = 'timeout_order';
  }
  const endpoint = config.endpoint.trim();
  if (transport === 'router_private') {
    if (!/^http:\/\/compute-router:18765\/?$/.test(config.endpoint))
      errors.endpoint = 'private_endpoint';
  } else if (endpoint || config.enabled) {
    try {
      const url = new URL(endpoint);
      if (
        endpoint.length > 2048 ||
        url.protocol !== 'https:' ||
        !url.hostname ||
        url.username ||
        url.password ||
        url.hash ||
        url.search ||
        url.port ||
        url.hostname === 'localhost' ||
        url.hostname.endsWith('.localhost')
      )
        throw new Error();
    } catch {
      errors.endpoint = 'https';
    }
  }
  if (config.enabled) {
    if (!(credential || (credentialConfigured && !clearCredential)))
      errors.credential = 'credential';
    if (!config.free_tier_confirmed) errors.free_tier_confirmed = 'free_tier';
    if (
      transport === 'router_private' &&
      !operations.some(([name]) => config.operations[name])
    )
      errors.operations = 'operation_required';
  }
  const replacement = credential;
  if (
    replacement &&
    (replacement.length < 32 ||
      replacement.length > 4096 ||
      /[^\x21-\x7e]/.test(replacement))
  ) {
    errors.credential = 'credential_size';
  }
  if (
    credentialConfigured &&
    endpoint !== savedEndpoint.trim() &&
    !replacement &&
    !clearCredential
  ) {
    errors.endpoint = 'endpoint_credential';
  }
  if (
    credentialConfigured &&
    transport !== (savedTransport || 'public_https') &&
    !replacement &&
    !clearCredential
  ) {
    errors.transport = 'transport_credential';
  }
  return errors;
}

export default function ComputeSettingsV110() {
  const { i18n } = useTranslation();
  const text = (zh, en) => (i18n?.language?.startsWith('zh') ? zh : en);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(() => publicConfig(computeDefaultsV110));
  const [credential, setCredential] = useState('');
  const [credentialMode, setCredentialMode] = useState('keep');
  const [busy, setBusy] = useState('load');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [notice, setNotice] = useState('');
  const [statusStale, setStatusStale] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [readinessCurrent, setReadinessCurrent] = useState(false);
  const [serverCode, setServerCode] = useState('');
  const request = useRef(0);
  const locked = useRef(false);
  const form = useRef(null);
  const dirty = Boolean(
    saved &&
      (JSON.stringify(draft) !== JSON.stringify(saved.config) ||
        credentialMode === 'clear' ||
        credential.length > 0)
  );
  const disabled = Boolean(busy) || !saved || needsReload;
  const privateSaved = saved?.config.transport === 'router_private';
  const probeDisabled =
    disabled ||
    !saved?.config.endpoint ||
    (!privateSaved && !saved?.config.free_tier_confirmed) ||
    !saved?.credential_configured;

  function begin(action) {
    if (locked.current) return null;
    locked.current = true;
    setBusy(action);
    setError('');
    setServerCode('');
    setNotice('');
    setCapabilities(null);
    setReadinessCurrent(false);
    return ++request.current;
  }
  function finish(ticket) {
    if (ticket !== request.current) return;
    locked.current = false;
    setBusy('');
  }
  function accept(next, replaceDraft, allowReadiness = true) {
    const changed =
      saved &&
      (JSON.stringify(next.config) !== JSON.stringify(saved.config) ||
        next.credential_configured !== saved.credential_configured);
    if (changed) {
      setCapabilities(null);
      setNotice('');
    }
    setSaved(next);
    setReadinessCurrent(allowReadiness);
    setStatusStale(false);
    if (replaceDraft) {
      setDraft(next.config);
      setCredential('');
      setCredentialMode('keep');
      setFieldErrors({});
    }
  }
  async function load(replaceDraft = false) {
    const ticket = begin('load');
    if (ticket === null) return;
    try {
      const next = snapshot(unwrap(await computeAPI.get(settingsEndpoint)));
      if (ticket !== request.current) return;
      accept(next, replaceDraft || !dirty || needsReload);
      setNeedsReload(false);
    } catch (failure) {
      if (ticket !== request.current) return;
      setStatusStale(true);
      setError('load');
      setServerCode(failure.message);
    } finally {
      finish(ticket);
    }
  }
  function invalidateRequests() {
    request.current++;
    locked.current = false;
  }
  useEffect(() => {
    load(true);
    return invalidateRequests;
    // A language change must not reload settings or erase a credential draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function edit(key, value) {
    if (disabled) return;
    setDraft((current) => ({ ...current, [key]: value }));
    setFieldErrors({});
    setError('');
    setServerCode('');
    setNotice('');
    setCapabilities(null);
    setReadinessCurrent(false);
  }
  function changeTransport(value) {
    if (disabled) return;
    edit('transport', value);
    setDraft((current) => ({
      ...current,
      endpoint:
        value === 'router_private'
          ? privateEndpoint
          : /^http:\/\/compute-router:18765\/?$/.test(current.endpoint)
          ? saved.config.transport === 'public_https'
            ? saved.config.endpoint
            : ''
          : current.endpoint,
    }));
  }
  function changeCredential(mode) {
    if (disabled) return;
    setCredentialMode(mode);
    setCredential('');
    setFieldErrors({});
    setError('');
    setServerCode('');
    setNotice('');
    setCapabilities(null);
    setReadinessCurrent(false);
  }
  function reset() {
    if (disabled) return;
    setDraft(saved.config);
    setCredential('');
    setCredentialMode('keep');
    setFieldErrors({});
    setError('');
    setServerCode('');
    setNotice('');
    setCapabilities(null);
    setReadinessCurrent(false);
  }
  async function save(event) {
    event.preventDefault();
    if (disabled || locked.current || !dirty) return;
    const errors = validateComputeSettingsV110(
      draft,
      saved.credential_configured,
      credential,
      credentialMode === 'clear',
      saved.config.endpoint,
      saved.config.transport
    );
    setFieldErrors(errors);
    setNotice('');
    if (Object.keys(errors).length) {
      setError('validation');
      const first = Object.keys(errors)[0];
      const target =
        form.current?.querySelector(`[name="${first}"]`) ||
        (first === 'operations' &&
          form.current?.querySelector('[name="statistics.model_rollup"]')) ||
        (first === 'credential' &&
          form.current?.querySelector('#compute-change-credential-v110'));
      target?.focus();
      return;
    }
    const config = publicConfig(draft);
    for (const [key] of numberFields) config[key] = Number(config[key]);
    config.endpoint = config.endpoint.trim();
    const body = { config };
    if (credentialMode === 'clear') body.clear_credential = true;
    else if (credential) body.credential = credential;
    const ticket = begin('save');
    let acknowledged = false;
    try {
      const data = unwrap(await computeAPI.put(settingsEndpoint, body));
      if (ticket !== request.current) return;
      acknowledged = true;
      // PUT may return the public snapshot or only an acknowledgement.
      setCredential('');
      setCredentialMode('keep');
      setCapabilities(null);
      const next = snapshot(
        data?.config ? data : unwrap(await computeAPI.get(settingsEndpoint))
      );
      if (ticket !== request.current) return;
      accept(next, true);
      setNeedsReload(false);
      setNotice('saved');
    } catch (failure) {
      if (ticket !== request.current) return;
      setError(acknowledged ? 'confirm' : 'save');
      setNeedsReload(acknowledged);
      setStatusStale(true);
      setServerCode(failure.message);
    } finally {
      finish(ticket);
    }
  }
  async function probe() {
    if (probeDisabled || locked.current) return;
    const ticket = begin('probe');
    let result;
    try {
      // No draft, secret or user payload is sent. The server owns the saved-settings probe.
      result = probeCapabilities(
        unwrap(await computeAPI.post(probeEndpoint)),
        privateSaved
      );
      if (ticket === request.current) {
        setCapabilities(result);
        setNotice('probed');
      }
    } catch (failure) {
      if (ticket === request.current) {
        setError('probe');
        setServerCode(failure.message);
      }
    }
    try {
      if (ticket !== request.current) return;
      const next = snapshot(unwrap(await computeAPI.get(settingsEndpoint)));
      if (ticket === request.current) {
        accept(next, !dirty, Boolean(result));
        if (
          result?.router &&
          ((next.status.router &&
            JSON.stringify(next.status.router) !==
              JSON.stringify(result.router)) ||
            [
              'unverified',
              'blocked',
              'open',
              'quota_exhausted',
              'unconfigured',
            ].includes(next.status.state))
        ) {
          setCapabilities(null);
          setNotice('');
        }
      }
    } catch {
      if (ticket === request.current) {
        setStatusStale(true);
        if (privateSaved || result?.router) {
          setCapabilities(null);
          setNotice('');
        }
      }
    } finally {
      finish(ticket);
    }
  }

  const errorCopy = {
    load: text(
      '无法读取设置，请重试。现有草稿已保留。',
      'Could not load settings. Retry; your draft is retained.'
    ),
    save: text(
      '未能确认保存成功。草稿已保留，请重试或刷新核对。',
      'Save was not confirmed. Your draft is retained; retry or refresh to verify.'
    ),
    confirm: text(
      '保存已接受，但未能读取结果。请重新读取确认。',
      'Save accepted, but the result could not be loaded. Reload to confirm.'
    ),
    probe: text(
      '探测未成功，请检查已保存的配置和运行状态。',
      'Probe failed. Check the saved configuration and runtime status.'
    ),
    validation: text(
      '请修正标出的设置后再保存。',
      'Correct the highlighted settings before saving.'
    ),
  };
  const serverCopy = {
    compute_requires_persistent_session_secret: text(
      '服务端需设置持久 SESSION_SECRET（至少 32 字符）。',
      'The server requires a persistent SESSION_SECRET (at least 32 characters).'
    ),
    compute_endpoint_change_requires_new_credential: text(
      '更改地址需更换或清除原凭据。',
      'Changing the endpoint requires replacing or clearing the credential.'
    ),
    compute_endpoint_requires_public_https: text(
      '服务地址必须使用公网 HTTPS 和 443 端口，不能含查询参数。',
      'Use a public HTTPS endpoint on port 443 without query parameters.'
    ),
    invalid_compute_transport: text(
      '请选择公网 HTTPS 或 Docker 内网路由模式。',
      'Select public HTTPS or Docker internal router transport.'
    ),
    compute_router_endpoint_invalid: text(
      'Docker 内网路由地址仅允许 http://compute-router:18765（可带结尾斜杠）。',
      'The Docker router endpoint must be http://compute-router:18765 with an optional trailing slash.'
    ),
    compute_router_address_not_private: text(
      '路由地址未解析到允许的内网地址，请检查 Docker 网络。',
      'The router did not resolve to an allowed private address. Check the Docker network.'
    ),
    router_protocol_mismatch: text(
      '路由协议不兼容，请检查路由版本。',
      'The router protocol is incompatible. Check the router version.'
    ),
    compute_router_contract_invalid: text(
      '路由就绪响应无效，请检查路由版本后重新探测。',
      'Router readiness response is invalid. Check the router version and probe again.'
    ),
    router_dispatch_disabled: text(
      '路由派发未启用。',
      'Router dispatch is disabled.'
    ),
    router_quota_unverified: text(
      '路由免费预算尚未就绪或无法验证。',
      'Router free-tier budgets are missing or unverified.'
    ),
    router_operation_unavailable: text(
      '所选算子未获路由就绪确认，请检查支持情况和预算。',
      'Selected operations are not ready on the router. Check support and budgets.'
    ),
    dispatch_disabled: text('路由派发未启用。', 'Router dispatch is disabled.'),
    quota_unknown: text(
      '路由免费预算尚未就绪或无法验证。',
      'Router free-tier budgets are missing or unverified.'
    ),
    quota_exhausted: text(
      '免费请求预算已耗尽。',
      'The free request budget is exhausted.'
    ),
    executor_unavailable: text(
      '路由执行器不可用。',
      'The router executor is unavailable.'
    ),
    executor_isolated: text(
      '路由执行器已隔离。',
      'The router executor is isolated.'
    ),
    rate_limited: text(
      '路由已限流，请稍后重新探测。',
      'The router is rate limited. Probe again later.'
    ),
    version_unsupported: text(
      '路由不支持所需算子版本。',
      'The router does not support the required operation version.'
    ),
    compute_operation_required: text(
      '请至少选择一个执行算子。',
      'Select at least one operation.'
    ),
    invalid_compute_credential: text(
      '凭据须为 32 到 4096 个可打印 ASCII 字符，不含任何空白。',
      'Use 32 to 4096 printable ASCII characters, with no whitespace.'
    ),
    compute_credential_required: text(
      '请提供服务凭据。',
      'Provide a service credential.'
    ),
    compute_credential_unavailable: text(
      '已存凭据无法解密，请检查服务端密钥或更换凭据。',
      'The saved credential cannot be decrypted. Check the server key or replace the credential.'
    ),
    compute_quota_exhausted: text(
      '当日请求额度已用尽。',
      'The daily request limit has been reached.'
    ),
    compute_not_configured: text(
      privateSaved
        ? '请先保存 Docker 内网路由地址和凭据。'
        : '请先保存服务地址、凭据和服务商免费额度耗尽硬停止确认。',
      privateSaved
        ? 'Save the Docker internal router endpoint and credential first.'
        : 'Save the endpoint, credential and provider free-tier hard-stop confirmation first.'
    ),
    compute_capacity_exhausted: text(
      '执行容量已满，请稍后重试。',
      'Compute capacity is full. Retry later.'
    ),
    credentials_rejected: text(
      '远端拒绝凭据，请更换凭据。',
      'The worker rejected the credential. Replace it.'
    ),
    unsupported_protocol: text(
      '远端协议不兼容。',
      'The worker protocol is incompatible.'
    ),
    root_required: text(
      '此设置仅限 Root 账号。',
      'These settings require a Root account.'
    ),
  };
  function fieldError(key) {
    const code = fieldErrors[key];
    if (!code) return undefined;
    let content;
    if (code === 'range') {
      const [, min, max] = numberFields.find(([name]) => name === key);
      content = text(
        `请输入 ${min} 到 ${max} 的整数。`,
        `Enter an integer from ${min} to ${max}.`
      );
    } else {
      content = {
        timeout_order: text(
          '连接超时不能超过远程超时。',
          'Connect timeout must not exceed remote timeout.'
        ),
        https: text(
          '请输入公网 HTTPS 地址（443 端口），不能含用户名、密码、查询参数或片段。',
          'Use an HTTPS URL on port 443 without user info, query or fragment.'
        ),
        private_endpoint: serverCopy.compute_router_endpoint_invalid,
        transport: serverCopy.invalid_compute_transport,
        operation_required: serverCopy.compute_operation_required,
        transport_credential: text(
          '更改连接模式需更换或清除原凭据。',
          'Changing the transport requires replacing or clearing the credential.'
        ),
        credential: text(
          '启用前需要保留或提供凭据。',
          'Keep or provide a credential before enabling.'
        ),
        credential_size: text(
          '凭据须为 32 到 4096 个可打印 ASCII 字符，不含任何空白。',
          'Use 32 to 4096 printable ASCII characters, with no whitespace.'
        ),
        endpoint_credential: text(
          '更改地址需更换或清除原凭据。',
          'Changing the endpoint requires replacing or clearing the credential.'
        ),
        free_tier: text(
          '启用前请确认服务商已配置免费额度耗尽硬停止。',
          'Confirm the provider hard stop on free-tier exhaustion before enabling.'
        ),
      }[code];
    }
    return { content, pointing: 'above' };
  }
  const status = saved?.status;
  const stateLabels = {
    disabled: text('已停用', 'Disabled'),
    ready: text('就绪', 'Ready'),
    unverified: text('路由尚未验证', 'Router unverified'),
    blocked: text('路由未就绪', 'Router blocked'),
    open: text('熔断中', 'Circuit open'),
    quota_exhausted: text('当日额度已用尽', 'Daily limit reached'),
    unconfigured: text('未配置完整', 'Unconfigured'),
  };
  const circuitDate =
    Number(status?.circuit_until) > 0
      ? new Date(Number(status.circuit_until) * 1000)
      : null;
  const safeServerCopy = (code) =>
    Object.prototype.hasOwnProperty.call(serverCopy, code)
      ? serverCopy[code]
      : '';
  const lastError = status?.last_error
    ? safeServerCopy(status.last_error) || text('未知错误', 'Unknown error')
    : text('无', 'None');
  // Runtime router metadata is authenticated backend evidence, never inferred from config.
  const router =
    capabilities?.router ||
    (readinessCurrent && !dirty && !busy && !statusStale && !error
      ? status?.router
      : null);
  const selectedOperations = operations.filter(
    ([name]) => saved?.config.operations[name]
  );
  const operationSupported = (name) => {
    if (!capabilities) return router?.ready_operations.includes(name);
    const schema = name.replace('statistics.', '').replaceAll('_', '-');
    return capabilities?.operations.some(
      (op) =>
        op.name === name &&
        op.version === '1.0.0' &&
        op.input_schema === `${schema}-input/1` &&
        op.output_schema === `${schema}-output/1`
    );
  };
  const routerExecutionBlocked = [
    'unverified',
    'blocked',
    'open',
    'quota_exhausted',
    'unconfigured',
  ].includes(status?.state);
  const routerReady = Boolean(
    router &&
      !routerExecutionBlocked &&
      router.dispatch_enabled &&
      router.quota_ready &&
      router.reason === 'ready' &&
      selectedOperations.length > 0 &&
      selectedOperations.every(
        ([name]) =>
          operationSupported(name) && router.ready_operations.includes(name)
      )
  );
  const routerReason = router?.reason
    ? safeServerCopy(router.reason) ||
      (router.reason === 'ready'
        ? ''
        : text(
            '路由返回未知原因，请检查路由配置后重新探测。',
            'The router reported an unknown reason. Check its configuration and probe again.'
          ))
    : '';
  const routerInspected = privateSaved || Boolean(router);

  return (
    <section
      className='compute-settings-v110'
      aria-labelledby='compute-heading-v110'
      aria-busy={Boolean(busy)}
    >
      <Header as='h3' id='compute-heading-v110'>
        {text('计算外置', 'Compute offload')}
      </Header>
      {error && (
        <Message
          negative
          visible
          role='alert'
          content={[
            errorCopy[error],
            Object.prototype.hasOwnProperty.call(serverCopy, serverCode) &&
              serverCopy[serverCode],
          ]
            .filter(Boolean)
            .join(' ')}
        />
      )}
      {!saved ? (
        <div role='status'>
          {busy ? (
            text('正在读取设置...', 'Loading settings...')
          ) : (
            <Button
              type='button'
              icon='refresh'
              content={text('重试', 'Retry')}
              onClick={() => load(true)}
            />
          )}
        </div>
      ) : (
        <>
          <section
            className='compute-status-v110'
            aria-labelledby='compute-status-heading-v110'
          >
            <div className='compute-toolbar-v110'>
              <Header as='h4' id='compute-status-heading-v110'>
                {text('运行状态', 'Runtime status')}
              </Header>
              <span role='status' className='compute-state-v110'>
                <Icon
                  name={
                    status.state === 'ready' && !privateSaved
                      ? 'check circle outline'
                      : 'circle outline'
                  }
                />
                {privateSaved && status.state === 'ready'
                  ? text('已配置', 'Configured')
                  : stateLabels[status.state] || text('未知', 'Unknown')}
              </span>
              <Button
                type='button'
                icon='refresh'
                title={text('刷新状态', 'Refresh status')}
                aria-label={text('刷新状态', 'Refresh status')}
                disabled={Boolean(busy)}
                loading={busy === 'load'}
                onClick={() => load()}
              />
            </div>
            {statusStale && (
              <Message
                warning
                visible
                role='status'
                content={text(
                  '状态可能已过期，请刷新。',
                  'Status may be outdated. Refresh to verify.'
                )}
              />
            )}
            {needsReload && (
              <Button
                type='button'
                icon='refresh'
                disabled={Boolean(busy)}
                content={text('重新读取', 'Reload')}
                onClick={() => load(true)}
              />
            )}
            <dl className='compute-counters-v110'>
              {counters.map(([key, zh, en]) => (
                <div key={key}>
                  <dt>{text(zh, en)}</dt>
                  <dd data-counter={key}>
                    {Number.isFinite(status[key]) && status[key] >= 0
                      ? status[key].toLocaleString(i18n?.language)
                      : key === 'requests_today' && !privateSaved
                      ? '0'
                      : '-'}
                    {key === 'requests_today' &&
                      ` / ${Number(
                        saved.config.daily_request_limit
                      ).toLocaleString(i18n?.language)}`}
                  </dd>
                </div>
              ))}
            </dl>
            <div className='compute-status-detail-v110'>
              <span>
                {text('最近错误', 'Last error')}: <code>{lastError}</code>
              </span>
              {circuitDate && Number.isFinite(circuitDate.getTime()) && (
                <span>
                  {text('熔断至', 'Circuit until')}:{' '}
                  <time dateTime={circuitDate.toISOString()}>
                    {circuitDate.toLocaleString(i18n?.language)}
                  </time>
                </span>
              )}
            </div>
          </section>
          <div ref={form}>
            <Form onSubmit={save} noValidate>
              <div className='compute-section-v110'>
                <Form.Checkbox
                  toggle
                  name='enabled'
                  id='compute-enabled-v110'
                  disabled={disabled}
                  label={text('启用计算外置', 'Enable compute offload')}
                  checked={draft.enabled}
                  onChange={(_, { checked }) => edit('enabled', checked)}
                />
                <Form.Field error={Boolean(fieldErrors.transport)}>
                  <label id='compute-transport-label-v113'>
                    {text('连接模式', 'Transport')}
                  </label>
                  <div
                    className='compute-transport-v113'
                    role='radiogroup'
                    aria-labelledby='compute-transport-label-v113'
                    aria-invalid={Boolean(fieldErrors.transport)}
                    aria-describedby={
                      fieldErrors.transport
                        ? 'compute-transport-error-v113'
                        : undefined
                    }
                  >
                    <Form.Radio
                      id='compute-transport-public-v113'
                      name='transport'
                      value='public_https'
                      label={text('公网 HTTPS', 'Public HTTPS')}
                      checked={draft.transport === 'public_https'}
                      disabled={disabled}
                      onChange={() => changeTransport('public_https')}
                    />
                    <Form.Radio
                      id='compute-transport-private-v113'
                      name='transport'
                      value='router_private'
                      label={text('Docker 内网路由', 'Docker internal router')}
                      checked={draft.transport === 'router_private'}
                      disabled={disabled}
                      onChange={() => changeTransport('router_private')}
                    />
                  </div>
                  {fieldErrors.transport && (
                    <div
                      id='compute-transport-error-v113'
                      className='ui pointing above prompt label'
                    >
                      {fieldError('transport').content}
                    </div>
                  )}
                </Form.Field>
                <Form.Input
                  name='endpoint'
                  id='compute-endpoint-v110'
                  type='url'
                  fluid
                  disabled={disabled}
                  label={
                    draft.transport === 'router_private'
                      ? text(
                          'Docker 内网路由地址',
                          'Docker internal router endpoint'
                        )
                      : text('HTTPS 服务地址', 'HTTPS endpoint')
                  }
                  value={draft.endpoint}
                  error={fieldError('endpoint')}
                  aria-invalid={Boolean(fieldErrors.endpoint)}
                  onChange={(_, { value }) => edit('endpoint', value)}
                />
                <div className='compute-credential-v110'>
                  <div className='compute-toolbar-v110'>
                    <strong>{text('服务凭据', 'Service credential')}</strong>
                    <span>
                      {saved.credential_configured
                        ? text('已配置', 'Configured')
                        : text('未配置', 'Not configured')}
                    </span>
                    <Button
                      type='button'
                      id='compute-change-credential-v110'
                      icon='key'
                      disabled={disabled || credentialMode === 'change'}
                      content={text('更换凭据', 'Change credential')}
                      onClick={() => changeCredential('change')}
                    />
                  </div>
                  {credentialMode === 'change' && (
                    <Form.Input
                      type='password'
                      autoComplete='new-password'
                      name='credential'
                      id='compute-credential-v110'
                      disabled={disabled}
                      label={text(
                        '新凭据（留空保留原值）',
                        'New credential (empty keeps existing)'
                      )}
                      value={credential}
                      error={fieldError('credential')}
                      aria-invalid={Boolean(fieldErrors.credential)}
                      onChange={(_, { value }) => {
                        if (disabled) return;
                        setCredential(value);
                        setFieldErrors({});
                        setError('');
                        setServerCode('');
                        setNotice('');
                        setCapabilities(null);
                        setReadinessCurrent(false);
                      }}
                    />
                  )}
                  {credentialMode !== 'change' && fieldErrors.credential && (
                    <Message
                      negative
                      visible
                      content={fieldError('credential').content}
                    />
                  )}
                  <Form.Checkbox
                    name='clear_credential'
                    id='compute-clear-v110'
                    disabled={disabled || !saved.credential_configured}
                    label={text(
                      '保存时清除已存凭据',
                      'Clear saved credential on save'
                    )}
                    checked={credentialMode === 'clear'}
                    onChange={(_, { checked }) =>
                      changeCredential(checked ? 'clear' : 'keep')
                    }
                  />
                  {credentialMode === 'change' && (
                    <Button
                      type='button'
                      basic
                      icon='cancel'
                      disabled={disabled}
                      content={text('取消更换', 'Cancel change')}
                      onClick={() => changeCredential('keep')}
                    />
                  )}
                </div>
              </div>
              <div className='compute-section-v110'>
                <Header as='h4'>{text('执行范围', 'Operations')}</Header>
                {fieldErrors.operations && (
                  <Message
                    negative
                    visible
                    content={fieldError('operations').content}
                  />
                )}
                <div className='compute-fields-v110'>
                  {operations.map(([key, zh, en]) => (
                    <Form.Checkbox
                      key={key}
                      id={`compute-${key}-v110`}
                      name={key}
                      disabled={disabled}
                      label={text(zh, en)}
                      checked={draft.operations[key]}
                      onChange={(_, { checked }) =>
                        edit('operations', {
                          ...draft.operations,
                          [key]: checked,
                        })
                      }
                    />
                  ))}
                </div>
              </div>
              <div className='compute-section-v110'>
                <Header as='h4'>
                  {text('资源与限额', 'Resources and limits')}
                </Header>
                <div className='compute-fields-v110'>
                  {numberFields.map(([key, min, max, zh, en]) => (
                    <Form.Input
                      key={key}
                      id={`compute-${key}-v110`}
                      name={key}
                      type='number'
                      inputMode='numeric'
                      min={min}
                      max={max}
                      step={1}
                      disabled={disabled}
                      label={text(zh, en)}
                      value={draft[key]}
                      error={fieldError(key)}
                      aria-invalid={Boolean(fieldErrors[key])}
                      onChange={(_, { value }) => edit(key, value)}
                    />
                  ))}
                </div>
                <Form.Checkbox
                  name='free_tier_confirmed'
                  id='compute-free-tier-v110'
                  disabled={disabled}
                  label={text(
                    '已确认服务商已配置免费额度耗尽硬停止',
                    'I confirm a provider-side hard stop on free-tier exhaustion is configured'
                  )}
                  checked={draft.free_tier_confirmed}
                  error={fieldError('free_tier_confirmed')}
                  onChange={(_, { checked }) =>
                    edit('free_tier_confirmed', checked)
                  }
                />
                <p className='compute-note-v110'>
                  {text(
                    '探测计入本地每日请求次数；此限额不等于服务商账单上限。',
                    'Probes count toward the local daily request limit; this is not a provider billing cap.'
                  )}
                </p>
              </div>
              {notice && (
                <Message
                  positive={
                    notice === 'saved' || !routerInspected || routerReady
                  }
                  warning={
                    notice === 'probed' && routerInspected && !routerReady
                  }
                  visible
                  role='status'
                  content={
                    notice === 'saved'
                      ? text('已保存', 'Saved')
                      : routerInspected
                      ? routerReady
                        ? text(
                            '路由已验证，已保存的所选算子就绪。',
                            'Router verified for the saved selected operations.'
                          )
                        : text(
                            '路由探测已完成，但尚未就绪。',
                            'Router inspected, but not ready.'
                          )
                      : text('探测已完成', 'Probe completed')
                  }
                />
              )}
              <div className='compute-actions-v110'>
                <Button
                  primary
                  type='submit'
                  icon='save'
                  disabled={disabled || !dirty}
                  loading={busy === 'save'}
                  content={text('保存', 'Save')}
                />
                <Button
                  type='button'
                  basic
                  icon='undo'
                  disabled={disabled || !dirty}
                  content={text('撤销更改', 'Discard changes')}
                  onClick={reset}
                />
                <Button
                  type='button'
                  icon='heartbeat'
                  disabled={probeDisabled}
                  loading={busy === 'probe'}
                  content={text('探测已保存配置', 'Probe saved settings')}
                  onClick={probe}
                />
                {dirty && (
                  <span role='status' className='compute-note-v110'>
                    {text('有未保存更改', 'Unsaved changes')}
                  </span>
                )}
              </div>
            </Form>
          </div>
          {routerInspected && (
            <section
              className='compute-section-v110'
              aria-labelledby='compute-router-heading-v113'
            >
              <Header as='h4' id='compute-router-heading-v113'>
                {text('已保存配置的路由就绪状态', 'Saved router readiness')}
              </Header>
              {!router ? (
                <p role='status'>
                  {text(
                    '路由尚未验证。保存配置不代表路由就绪。',
                    'Router readiness is unverified. Saved settings do not establish readiness.'
                  )}
                </p>
              ) : (
                <>
                  <dl className='compute-counters-v110'>
                    <div>
                      <dt>{text('路由派发', 'Router dispatch')}</dt>
                      <dd>
                        {router.dispatch_enabled
                          ? text('已启用', 'Enabled')
                          : text('未启用', 'Disabled')}
                      </dd>
                    </div>
                    <div>
                      <dt>
                        {text('路由免费预算', 'Router free-tier budgets')}
                      </dt>
                      <dd>
                        {router.quota_ready
                          ? text('已验证就绪', 'Verified ready')
                          : text('缺失或尚未验证', 'Missing or unverified')}
                      </dd>
                    </div>
                    <div>
                      <dt>{text('所选算子', 'Selected operations')}</dt>
                      <dd data-router-readiness='selected'>
                        {routerReady
                          ? text('最近验证时就绪', 'Ready at last check')
                          : text('未就绪', 'Not ready')}
                      </dd>
                    </div>
                  </dl>
                  {routerExecutionBlocked && (
                    <p role='status'>{stateLabels[status.state]}</p>
                  )}
                  {routerReason && <p role='status'>{routerReason}</p>}
                  {!selectedOperations.length && (
                    <p role='status'>{serverCopy.compute_operation_required}</p>
                  )}
                  <ul className='compute-capabilities-v110'>
                    {selectedOperations.map(([name, zh, en]) => (
                      <li key={name} data-router-operation={name}>
                        <strong>{text(zh, en)}</strong>:{' '}
                        {capabilities && !operationSupported(name)
                          ? text(
                              '不支持或协议不兼容',
                              'Unsupported or incompatible'
                            )
                          : !router.ready_operations.includes(name)
                          ? text('尚未就绪', 'Not ready')
                          : !router.dispatch_enabled
                          ? text('派发未启用', 'Dispatch disabled')
                          : !router.quota_ready
                          ? text('预算尚未验证', 'Budget unverified')
                          : routerReady
                          ? text('最近验证时就绪', 'Ready at last check')
                          : text('尚未就绪', 'Not ready')}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}
          {capabilities && (
            <section
              className='compute-section-v110'
              aria-labelledby='compute-capabilities-v110'
            >
              <Header as='h4' id='compute-capabilities-v110'>
                {text('探测能力', 'Probed capabilities')}
              </Header>
              <dl className='compute-counters-v110'>
                <div>
                  <dt>{text('协议', 'Protocol')}</dt>
                  <dd>{String(capabilities.protocol).slice(0, 128)}</dd>
                </div>
                <div>
                  <dt>{text('Worker 版本', 'Worker build')}</dt>
                  <dd>
                    {String(capabilities.worker_build || '-').slice(0, 128)}
                  </dd>
                </div>
                <div>
                  <dt>
                    {text(
                      '最大输入 / 输出（字节）',
                      'Max input / output (bytes)'
                    )}
                  </dt>
                  <dd>
                    {Number(capabilities.max_input_bytes) || 0} /{' '}
                    {Number(capabilities.max_output_bytes) || 0}
                  </dd>
                </div>
              </dl>
              <ul className='compute-capabilities-v110'>
                {capabilities.operations.slice(0, 4).map((op, index) => (
                  <li key={index}>
                    <strong>{String(op.name || '').slice(0, 128)}</strong>{' '}
                    <span>{String(op.version || '').slice(0, 64)}</span>
                    <div>
                      {String(op.input_schema || '').slice(0, 128)} /{' '}
                      {String(op.output_schema || '').slice(0, 128)}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </section>
  );
}
